'use client';
import React, { useState, useRef } from 'react';
import UploadZone from '@/components/ui/UploadZone';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import SuccessAnimation from '@/components/ui/SuccessAnimation';

interface PDFFile {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number | null;
  loadingPages: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function PDFIcon() {
  return (
    <div className="w-9 h-10 rounded-lg bg-red-50 border border-red-100 flex flex-col items-center justify-center flex-shrink-0">
      <span className="text-[8px] font-black text-red-500 leading-none">PDF</span>
    </div>
  );
}

export default function MergePDFTool() {
  const [files, setFiles] = useState<PDFFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ size: number; pageCount: number; url: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPageCount = async (pdfFile: PDFFile) => {
    try {
      const { PDFDocument } = await import('pdf-lib');
      const buf = await pdfFile.file.arrayBuffer();
      let pdf = await PDFDocument.load(buf);
      setFiles((prev) =>
        prev.map((f) => f.id === pdfFile.id ? { ...f, pageCount: pdf.getPageCount(), loadingPages: false } : f)
      );
    } catch {
      setFiles((prev) =>
        prev.map((f) => f.id === pdfFile.id ? { ...f, pageCount: null, loadingPages: false } : f)
      );
    }
  };

  const addFiles = async (newFiles: FileList | null) => {
    if (!newFiles) return;
    setError(null);
    const pdfs = Array.from(newFiles).filter((f) => f.type === 'application/pdf');
    const nonPdfs = Array.from(newFiles).filter((f) => f.type !== 'application/pdf');
    if (nonPdfs.length > 0) {
      setError(`${nonPdfs.length} file(s) skipped — only PDF files are supported.`);
    }
    if (pdfs.length === 0) return;

    const newEntries: PDFFile[] = pdfs.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
      file: f,
      name: f.name,
      size: f.size,
      pageCount: null,
      loadingPages: true,
    }));

    setFiles((prev) => [...prev, ...newEntries]);
    setResult(null);

    // Load page counts asynchronously
    for (const entry of newEntries) {
      loadPageCount(entry);
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setResult(null);
    setError(null);
  };

  const moveFile = (index: number, direction: 'up' | 'down') => {
    const newFiles = [...files];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFiles.length) return;
    [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];
    setFiles(newFiles);
    setResult(null);
  };

  // Drag-to-reorder handlers
  const handleDragStart = (index: number) => setDragItemIndex(index);
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (dragItemIndex === null || dragItemIndex === dropIndex) {
      setDragOverIndex(null);
      setDragItemIndex(null);
      return;
    }
    const newFiles = [...files];
    const [moved] = newFiles.splice(dragItemIndex, 1);
    newFiles.splice(dropIndex, 0, moved);
    setFiles(newFiles);
    setDragOverIndex(null);
    setDragItemIndex(null);
    setResult(null);
  };
  const handleDragEnd = () => {
    setDragOverIndex(null);
    setDragItemIndex(null);
  };

  const totalPages = files.reduce((sum, f) => sum + (f.pageCount ?? 0), 0);

  const handleMerge = async () => {
    if (files.length < 2) {
      setError('Please add at least 2 PDF files to merge.');
      return;
    }
    setError(null);
    setResult(null);
    setProcessing(true);
    setProgress(0);
    setProgressLabel('Initializing…');

    try {
      const { PDFDocument } = await import('pdf-lib');
      const mergedPdf = await PDFDocument.create();

      for (let i = 0; i < files.length; i++) {
        setProgressLabel(`Processing file ${i + 1} of ${files.length}: ${files[i].name}`);
        setProgress(Math.round(((i + 0.5) / files.length) * 90));
        const arrayBuffer = await files[i].file.arrayBuffer();
        let pdf;
        try {
          pdf = await PDFDocument.load(arrayBuffer);
        } catch {
          throw new Error(`Could not read "${files[i].name}". The file may be corrupted or password-protected.`);
        }
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => mergedPdf.addPage(page));
        setProgress(Math.round(((i + 1) / files.length) * 90));
      }

      setProgressLabel('Saving merged PDF…');
      setProgress(95);
      const pdfBytes = await mergedPdf.save();
      setProgress(100);
      setProgressLabel('Done!');

      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setResult({ size: blob.size, pageCount: mergedPdf.getPageCount(), url });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to merge PDFs. Please try again.';
      setError(msg);
    } finally {
      setProcessing(false);
      setProgress(0);
      setProgressLabel('');
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = 'merged.pdf';
    a.click();
  };

  const handleReset = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    setFiles([]);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-5">
      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Success result */}
      {result && (
        <div className="success-banner animate-fade-in-up">
          <SuccessAnimation size="md" message="Merge complete!" />
          <div className="flex-1 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white/70 rounded-xl p-3 text-center">
                <p className="text-text-muted text-xs mb-1">Files merged</p>
                <p className="font-bold text-text-primary">{files.length}</p>
              </div>
              <div className="bg-white/70 rounded-xl p-3 text-center">
                <p className="text-text-muted text-xs mb-1">Total pages</p>
                <p className="font-bold text-text-primary">{result.pageCount}</p>
              </div>
              <div className="bg-white/70 rounded-xl p-3 text-center">
                <p className="text-text-muted text-xs mb-1">File size</p>
                <p className="font-bold text-brand-blue">{formatBytes(result.size)}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDownload}
                className="btn-action flex-1 py-3 text-sm font-semibold rounded-xl"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download merged.pdf
              </button>
              <button
                onClick={handleReset}
                className="btn-press px-4 py-3 rounded-xl border border-gray-200 text-text-secondary hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Start over
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload zone */}
      <UploadZone
        onFiles={addFiles}
        accept="application/pdf"
        multiple
        title="Add PDF Files"
        subtitle="Drag & drop or click to browse · Multiple files supported"
        hint="Only PDF files are supported"
        icon={
          <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        }
      />

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2 animate-fade-in-up">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-text-primary">
              {files.length} file{files.length !== 1 ? 's' : ''}
              {totalPages > 0 && <span className="text-text-muted font-normal"> · {totalPages} pages total</span>}
            </p>
            <p className="text-xs text-text-muted">Drag rows or use arrows to reorder</p>
          </div>

          {files.map((f, i) => (
            <div
              key={f.id}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 bg-white rounded-xl px-3 py-3 border transition-all cursor-grab active:cursor-grabbing ${
                dragOverIndex === i && dragItemIndex !== i
                  ? 'border-brand-blue bg-blue-50 shadow-sm'
                  : 'border-gray-100 hover:border-gray-200'
              } ${dragItemIndex === i ? 'opacity-50' : 'opacity-100'}`}
            >
              {/* Drag handle */}
              <div className="flex-shrink-0 text-gray-300 hover:text-gray-400">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 6a2 2 0 100-4 2 2 0 000 4zm0 8a2 2 0 100-4 2 2 0 000 4zm0 8a2 2 0 100-4 2 2 0 000 4zm8-16a2 2 0 100-4 2 2 0 000 4zm0 8a2 2 0 100-4 2 2 0 000 4zm0 8a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
              </div>

              {/* Order badge */}
              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-text-secondary">{i + 1}</span>
              </div>

              <PDFIcon />

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">{f.name}</p>
                <p className="text-xs text-text-muted">
                  {formatBytes(f.size)}
                  {f.loadingPages && (
                    <span className="ml-1 inline-flex items-center gap-1 text-gray-400">
                      · <LoadingSpinner size="sm" /> loading pages…
                    </span>
                  )}
                  {!f.loadingPages && f.pageCount !== null && (
                    <span className="ml-1">· {f.pageCount} page{f.pageCount !== 1 ? 's' : ''}</span>
                  )}
                  {!f.loadingPages && f.pageCount === null && (
                    <span className="ml-1 text-amber-500">· could not read pages</span>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => moveFile(i, 'up')}
                  disabled={i === 0}
                  title="Move up"
                  className="btn-press w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center disabled:opacity-25 transition-colors"
                >
                  <svg className="w-3.5 h-3.5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                  </svg>
                </button>
                <button
                  onClick={() => moveFile(i, 'down')}
                  disabled={i === files.length - 1}
                  title="Move down"
                  className="btn-press w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center disabled:opacity-25 transition-colors"
                >
                  <svg className="w-3.5 h-3.5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>
                <button
                  onClick={() => removeFile(f.id)}
                  title="Remove"
                  className="btn-press w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center transition-colors"
                >
                  <svg className="w-3.5 h-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {processing && (
        <div className="space-y-2 animate-fade-in-up">
          <div className="flex justify-between items-center text-sm">
            <span className="flex items-center gap-2 text-text-secondary font-medium truncate pr-2">
              <LoadingSpinner size="sm" />
              {progressLabel}
            </span>
            <span className="text-brand-blue font-bold flex-shrink-0">{progress}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="progress-bar-animated h-full rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Merge button */}
      {!result && (
        <button
          onClick={handleMerge}
          disabled={files.length < 2 || processing}
          className="btn-action w-full text-base py-3.5 rounded-xl"
        >
          {processing ? (
            <>
              <LoadingSpinner size="sm" variant="white" />
              Merging…
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 3M21 7.5H7.5" />
              </svg>
              Merge {files.length > 0 ? `${files.length} PDFs` : 'PDFs'}
              {files.length < 2 && files.length > 0 && <span className="text-xs opacity-70 ml-1">(need at least 2)</span>}
            </>
          )}
        </button>
      )}
    </div>
  );
}
